import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import { wpClient } from '../lib/wordpress';
import VideoEmbed from '../components/VideoEmbed';
import { FaStar } from 'react-icons/fa';

const GET_HACK_DETAIL = gql`
  query GetHackDetail($id: ID!) {
    disabilityHack(id: $id, idType: DATABASE_ID) {
      databaseId
      title
      categories {
        nodes {
          name
        }
      }
      disabilityHacksImport {
        title
        platform
        videoUrl
        summary
        hackDescription
        otKeywords {
          name
        }
        source
        userTested
        otUsefulnessRating
        hackType
        reviewedBy
        difficultyLevel
        featured
        submittedByEmail
      }
    }
  }
`;

interface HackDetail {
  databaseId: number;
  title: string;
  categories: {
    nodes: { name: string }[];
  };
  disabilityHacksImport: {
    title: string;
    platform: string;
    videoUrl: string;
    summary: string;
    hackDescription: string;
    otKeywords: { name: string }[];
    source: string;
    userTested: string;
    otUsefulnessRating: number;
    hackType: string;
    reviewedBy: string;
    difficultyLevel: string;
    featured: boolean;
    submittedByEmail: string;
  };
}

const HackDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { loading, error, data } = useQuery(GET_HACK_DETAIL, {
    variables: { id },
    client: wpClient
  });

  if (loading) return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-grow container mx-auto px-4 py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      </main>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-grow container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto p-6 bg-red-50 border border-red-200 rounded-lg">
          <h2 className="text-xl font-bold text-red-800 mb-4">Error Loading Hack</h2>
          <p className="text-red-700">{error.message}</p>
        </div>
      </main>
    </div>
  );

  const hack = data?.disabilityHack as HackDetail;
  if (!hack) return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-grow container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h2 className="text-xl font-bold text-yellow-800 mb-4">Hack Not Found</h2>
          <p className="text-yellow-700">The requested hack could not be found.</p>
        </div>
      </main>
    </div>
  );

  const { disabilityHacksImport: d, title } = hack;
  const usefulness = Number(d.otUsefulnessRating) || 0;

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-grow container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <Link to="/browse" className="text-blue-600 hover:underline">
              ← Back to Browse
            </Link>
          </div>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-6 space-y-6">
              {/* Category */}
              {(hack.categories?.nodes && hack.categories.nodes.length > 0 && hack.categories.nodes[0].name.toLowerCase() !== 'uncategorized') && (
                <div className="text-green-700 font-medium uppercase text-sm">
                  {hack.categories.nodes.map(cat => cat.name).join(', ')}
                </div>
              )}

              {/* Title */}
              <h1 className="text-3xl font-bold text-gray-900">
                {d.title || title}
              </h1>

              {/* Platform */}
              {d.platform && (
                <div>
                  <span className="font-semibold">Platform: </span>
                  <span>{d.platform}</span>
                </div>
              )}

              {/* Video */}
              {d.videoUrl && (
                <div>
                  <h2 className="text-xl font-semibold mb-2">Video</h2>
                  <VideoEmbed url={d.videoUrl} />
                </div>
              )}

              {/* Summary */}
              {d.summary && (
                <div>
                  <h2 className="text-xl font-semibold mb-2">Summary</h2>
                  <p className="text-gray-700">{d.summary}</p>
                </div>
              )}

              {/* Description */}
              {d.hackDescription && (
                <div>
                  <h2 className="text-xl font-semibold mb-2">Description</h2>
                  <p className="text-gray-700">
                    {(() => {
                      const words = d.hackDescription.split(/\s+/);
                      if (words.length > 150) {
                        return words.slice(0, 150).join(' ') + '...';
                      }
                      return d.hackDescription;
                    })()}
                  </p>
                </div>
              )}

              {/* Usefulness Rating */}
              {d.otUsefulnessRating && (
                <div>
                  <span className="font-semibold">Usefulness: </span>
                  {[1,2,3,4,5].map(i => (
                    <FaStar
                      key={i}
                      color={i <= usefulness ? "#fbbf24" : "#e5e7eb"}
                      className="inline-block"
                    />
                  ))}
                </div>
              )}

              {/* User Tested */}
              {d.userTested && (
                <div>
                  <span className="font-semibold">User Tested: </span>
                  <span>{d.userTested}</span>
                </div>
              )}

              {/* Keywords */}
              {d.otKeywords && d.otKeywords.length > 0 && (
                <div>
                  <span className="font-semibold">Keywords: </span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {d.otKeywords.map(keyword => (
                      <span key={keyword.name} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {keyword.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Source */}
              {d.source && (
                <div>
                  <span className="font-semibold">Source: </span>
                  <span>{d.source}</span>
                </div>
              )}

              {/* Reviewed By */}
              {d.reviewedBy && (
                <div>
                  <span className="font-semibold">Reviewed By: </span>
                  <span>{d.reviewedBy}</span>
                </div>
              )}

              {/* Difficulty Level */}
              {d.difficultyLevel && (
                <div>
                  <span className="font-semibold">Difficulty Level: </span>
                  <span>{d.difficultyLevel}</span>
                </div>
              )}

              {/* Featured */}
              {d.featured && (
                <div>
                  <span className="font-semibold">Featured Hack</span>
                </div>
              )}

              {/* Submitted By Email */}
              {d.submittedByEmail && (
                <div>
                  <span className="font-semibold">Submitted By: </span>
                  <span>{d.submittedByEmail}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HackDetail;
